import { FC, Key, useState } from 'react'
import {
  Button,
  Input,
  Spin,
  TextField,
  useGlobalState,
  useMessage,
} from '@pepper/ui'
import { Icon } from '@iconify/react'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import clsx from 'clsx'
import { show as showModal, useModal } from '@ebay/nice-modal-react'
import { CSVLink } from 'react-csv'

import BottomPopup, { BottomPopupBtn } from 'components/BottomPopup'
import CountrySelector from 'container/SEOTools/components/CountrySelector/CountrySelector'
import { CreateItemDropdown } from 'components/CreateItemDropdown/CreateItemDropdown'
import CreateItemModal from 'components/CreateItemModal/CreateItemModal'
import { urlRefreshValidationSchema } from './schema'
import RecentReportsTable from './RecentReportsTable'
import OrderingReportList from './OrderingReportList/OrderingReportList'
import Banner from '../Banner'
import { useContentRefreshParams } from '../hooks/useContentRefreshParams'
import { useGenerateReport } from 'container/SEOTools/hooks/useGenerateReport'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { useDeleteReport } from 'container/SEOTools/hooks/useDeleteReport'

import s from './UrlRefresh.module.css'

import type { GapReport } from '@pepper/utils/src/types/seo'
import GapDrawer from '../components/GapDrawer/GapDrawer'

type FormValues = {
  url: string
  keyword: string
  country: SEOCountries
}

type Props = {
  dataSource: GapReport[]
  isLoading: boolean
  isGeneratedReportsTab: boolean
  setPage: (page: number) => void
  setUrlSearch: React.Dispatch<React.SetStateAction<string>>
  limit: number
  handleRefetch?: () => void
  totalRecords: number
  page: number
}

const SUCCESS_STATUS = 3

type InitialGapReportValue = {
  reportId: string | null
  documentId: string | null
}
export const UrlRefresh: FC<Props> = ({
  isGeneratedReportsTab,
  dataSource,
  isLoading,
  setPage,
  handleRefetch,
  totalRecords,
  limit,
  setUrlSearch,
  page,
}) => {
  const { setQuery } = useContentRefreshParams()
  const [isCRRDrawerOpen, setIsCRRDrawerOpen] = useState(false)
  const [currentGapReport, setCurrentGapReport] =
    useState<InitialGapReportValue>({
      reportId: null,
      documentId: null,
    })
  const { mutateAsync: generateReport, isLoading: isGeneratingReports } =
    useGenerateReport()
  const companyId = useSelectedCompanyId()

  const createProjectModal = useModal(CreateItemModal)

  const { showError, showSuccess } = useMessage()
  const { mutateAsync: handleDelete, isLoading: isDeletingReport } =
    useDeleteReport()
  const { subscription, trialExhaust, permissions } = useGlobalState()
  const [selectedRows, setSelectedRows] = useState<React.Key[]>([])

  const { domains, isLoading: isLoadingDomains } = useContentRefreshParams()

  const cmpAllowed = permissions?.cmp

  const isAnyDomainConnectedToGsc =
    !isLoadingDomains && domains?.some(d => !!d.gsc_domain_property)

  const initialValues = {
    url: '',
    keyword: '',
    country: 'us' as SEOCountries,
  }

  const handleGenerateReport = async (
    values: FormValues,
    helper: FormikHelpers<FormValues>
  ) => {
    const isTrialExpired = subscription?.trialStatus === 'expired'
    const isLimitExhausted = trialExhaust?.seoContentRefresh

    if (!cmpAllowed || isTrialExpired || isLimitExhausted) {
      showModal('buy-cmp', {
        ...(isLimitExhausted && {
          id: 'seoContentRefresh',
        }),
      })
      return
    }
    const payload = {
      page_url: values.url,
      target_keyword: values.keyword,
      region_code: values.country,
      report_id: values,
    }
    const response = await generateReport({
      payload: [payload],
      companyId,
    })

    if (response?.error) {
      showError(response.error)
    } else {
      showSuccess('Report generation started')
      helper.resetForm()
    }
  }

  const handleDeleteReport = async (reportId: Key[]) => {
    const response = await handleDelete({
      companyId,
      reportIds: reportId.join(','),
    })

    if (response?.error) {
      showError(response.error)
    } else {
      showSuccess('Selected report(s) have been deleted')
      setSelectedRows([])
    }
  }

  const handleOrderContent = async (type: 'private_doc' | 'assignment') => {
    const DEFAULT_VERTICAL_ID = 20
    const selectedItems = dataSource.filter(item =>
      selectedRows.includes(item.id)
    )

    createProjectModal.show({
      items: selectedItems.map(item => ({
        content_gap_report_id: item.id,
        title: item.page_title,
        word_count: item.page_length?.toString(),
      })),
      type,
      defaultValues: { verticalId: DEFAULT_VERTICAL_ID },
      preFormBody: <OrderingReportList reports={selectedItems} />,
    })
  }

  const handleUrlSearch = (event: { target: { value: string } }) => {
    const searchValue = event.target.value
    setPage(1)
    setUrlSearch(searchValue)
  }

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber)
    setSelectedRows([])
  }

  const csvHeaders = [
    { label: 'Page Title', key: 'page_title' },
    { label: 'Page URL', key: 'page_url' },
    { label: 'Current Traffic', key: 'traffic' },
    { label: 'Potential Traffic', key: 'potential_traffic' },
  ]

  const handleReportClick = (reportId: string, documentId: string) => {
    setCurrentGapReport({
      reportId,
      documentId,
    })
    setIsCRRDrawerOpen(true)
  }

  const csvItems = dataSource.filter(i => selectedRows.includes(i.id))

  return (
    <>
      {!isGeneratedReportsTab && (
        <div className={s.searchBarWrapper}>
          <div className={s.searchContainer}>
            <Icon icon="uil:file-search-alt" className={s.keywordIcon} />
            <div className={s.searchBarHeader}>
              Scale your website&lsquo;s traffic effortlessly with your existing
              content
            </div>

            <div className={s.searchWrapper}>
              <Formik
                initialValues={initialValues}
                onSubmit={handleGenerateReport}
                validationSchema={urlRefreshValidationSchema}
              >
                {props => (
                  <Form className={s.form} data-id="page-refresh-input-fields">
                    <div className={s.input}>
                      <div className={s.searchBarTitle}>
                        Enter URL to analyse
                      </div>
                      <TextField
                        fieldProps={{
                          placeholder: 'Example: https://peppercontent.io',
                          value: props.values.url,
                          onChange: (element: {
                            target: { value: string }
                          }) => {
                            props.setFieldValue('url', element.target.value)
                          },
                          type: 'text',
                        }}
                        className={s.targetInput}
                        name="url"
                        onBlur={props.handleBlur}
                      />
                    </div>
                    <div className={s.input}>
                      <div className={s.searchBarTitle}>
                        Targeted Keyword (Optional)
                      </div>
                      <TextField
                        fieldProps={{
                          placeholder: 'Example: artificial Intelligence',
                          value: props.values.keyword,
                          onChange: (element: {
                            target: { value: string }
                          }) => {
                            props.setFieldValue('keyword', element.target.value)
                          },
                        }}
                        className={s.targetInput}
                        name="keyword"
                        onBlur={props.handleBlur}
                      />
                    </div>
                    <Field name="country" as="div">
                      <CountrySelector
                        value={props.values.country}
                        setValue={value => {
                          setQuery({
                            country: value,
                          })
                          props.setFieldValue('country', value)
                        }}
                      />
                    </Field>
                    <Button
                      disabled={!props.dirty || !props.isValid}
                      type="ghost"
                      size="large"
                      htmlType="submit"
                      data-id="page-refresh-generate-report-button"
                    >
                      Generate Report
                    </Button>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
      <div
        className={clsx(s.reportsTable, {
          [s.generatedReportsWrapper]: isGeneratedReportsTab,
        })}
      >
        {isGeneratedReportsTab && (
          <div className={s.header}>
            <div>
              <div className={s.headerText}>Content gap analysis reports</div>
              <div className={s.subHeaderText}>
                Here is a chronologically sorted list of all the content gap
                reports you’ve generated so far
              </div>
            </div>
            <div>
              <Input
                type="search"
                placeholder="Search a url"
                className={s.searchInput}
                prefix={<Icon icon="lucide:search" className={s.searchIcon} />}
                onChange={handleUrlSearch}
              />
            </div>
          </div>
        )}
        <Spin spinning={isLoading || isGeneratingReports || isDeletingReport}>
          <RecentReportsTable
            dataSource={dataSource}
            handleDelete={(reportId: string) => handleDeleteReport([reportId])}
            handleReportClick={handleReportClick}
            totalRecords={totalRecords}
            pageSize={limit}
            onPageChange={handlePageChange}
            page={page}
            selectedRowKeys={selectedRows}
            onRowSelect={ids => setSelectedRows(ids)}
          />

          {currentGapReport.reportId && currentGapReport.documentId ? (
            <GapDrawer
              open={isCRRDrawerOpen}
              reportId={currentGapReport.reportId}
              documentId={currentGapReport.documentId}
              onClose={() => {
                setIsCRRDrawerOpen(false)
              }}
            />
          ) : null}

          <BottomPopup
            selectedCount={selectedRows.length}
            onClose={() => setSelectedRows([])}
          >
            <CreateItemDropdown
              onCreateOrderClick={() => handleOrderContent('assignment')}
              onCreatePrivateDocClick={() => handleOrderContent('private_doc')}
              disabled={
                csvItems.filter(
                  items =>
                    items.item_id ??
                    items.generation_status_id !== SUCCESS_STATUS
                ).length > 0
              }
            >
              <BottomPopupBtn>Revamp Content</BottomPopupBtn>
            </CreateItemDropdown>
            <BottomPopupBtn icon={<Icon icon="lucide:download" />}>
              <CSVLink
                className={s.csvLink}
                data={csvItems}
                headers={csvHeaders}
                filename="URL Refresh"
              >
                Download
              </CSVLink>
            </BottomPopupBtn>
            <BottomPopupBtn
              onClick={() => handleDeleteReport(selectedRows)}
              isLoading={isDeletingReport}
            />
          </BottomPopup>
        </Spin>
      </div>
      {!isGeneratedReportsTab && !isAnyDomainConnectedToGsc && (
        <Banner tab="URL_REFRESH" handleRefetch={handleRefetch} />
      )}
    </>
  )
}
